'use client';

import { createContext, useEffect, useMemo } from 'react';
import { CurrentTimeEmitter } from './CurrentTimeEmitter';

/**
 * Current Time Emitter Context. Used to provide the CurrentTimeEmitter instance.
 * @internal
 */
const Context = createContext<CurrentTimeEmitter | null>(null);

type ProviderProps = {
  children: React.ReactNode;
  ssrTime: number;
};

/**
 * Internal provider client component that creates a CurrentTimeEmitter with the ssrTime to be passed as context. Necessary for the server component to be able to provide the value.
 * @internal
 */
function Provider({ children, ssrTime }: ProviderProps) {
  const currentTimeEmitter = useMemo(() => new CurrentTimeEmitter(ssrTime), [ssrTime]);

  useEffect(() => {
    currentTimeEmitter.restart();
    return () => currentTimeEmitter.stop();
  }, [currentTimeEmitter]);

  return <Context.Provider value={currentTimeEmitter}>{children}</Context.Provider>;
}

export { Context, Provider, type ProviderProps };
